/**
 * Hook `useUpdateMe` — TH-55255 / área do usuário.
 *
 * Chama PUT /api/auth/me para atualizar os campos editáveis do usuário
 * autenticado (atualmente só `name`). Invalida `["auth", "me"]` em
 * onSuccess para que componentes que consomem `useAuth` recebam os dados
 * frescos sem reload.
 *
 * O backend usa `extra="ignore"` no schema: campos sensíveis (role,
 * tenant_id, email) são descartados silenciosamente mesmo que enviados.
 */

import { useMutation, useQueryClient } from "@tanstack/react-query";

export interface UpdateMePayload {
  name: string;
}

async function updateMe(payload: UpdateMePayload): Promise<void> {
  const res = await fetch("/api/auth/me", {
    method: "PUT",
    credentials: "include",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { detail?: string };
    throw new Error(body.detail ?? `HTTP ${res.status}`);
  }
}

export function useUpdateMe() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateMe,
    onSuccess: () => {
      // Invalida o cache do usuário atual para forçar re-fetch em todos
      // os componentes que dependem de useAuth (AppLayout, Topbar, etc.).
      void queryClient.invalidateQueries({ queryKey: ["auth", "me"] });
    },
  });
}
